import React, { useState } from 'react';
import { MapPin, Plane, Clock, ChevronRight, ChevronLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { vehicleTypes, occasions } from '../data/mock';

const WHATSAPP_NUMBER = '221774884254';

const BookingForm = () => {
  const [activeTab, setActiveTab] = useState('simple');
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    departure: '',
    arrival: '',
    date: '',
    time: '',
    flightNumber: '',
    hours: '',
    passengers: '',
    vehicleType: '',
    occasion: '',
    message: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: ''
  });

  const tabs = [
    { id: 'simple', label: 'Transfert simple', icon: MapPin },
    { id: 'airport', label: 'Transfert aéroport', icon: Plane },
    { id: 'hourly', label: 'Mise à disposition', icon: Clock }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };

  const getServiceType = () => {
    const serviceTypes = {
      simple: 'Transfert simple',
      airport: 'Transfert aéroport',
      hourly: 'Mise à disposition'
    };
    return serviceTypes[activeTab] || 'Transfert';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const serviceType = getServiceType();
    const arrival = formData.arrival || formData.hours + ' heures';

    let message = `━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `    🚘 *BARHAM DRIVER*\n`;
    message += `    _Réservation VTC_\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━\n\n`;

    message += `📋 *Service:* ${serviceType}\n\n`;

    message += `┌─────────────────────\n`;
    message += `│ 👤 *INFORMATIONS CLIENT*\n`;
    message += `├─────────────────────\n`;
    message += `│ Nom: ${formData.firstName}\n`;
    if (formData.company) message += `│ Entreprise: ${formData.company}\n`;
    message += `│ Tél: ${formData.phone}\n`;
    message += `│ Email: ${formData.email}\n`;
    message += `└─────────────────────\n\n`;

    message += `┌─────────────────────\n`;
    message += `│ 🗺️ *DÉTAILS DU TRAJET*\n`;
    message += `├─────────────────────\n`;
    message += `│ 📍 Départ: ${formData.departure}\n`;
    message += `│ 🏁 Arrivée: ${arrival}\n`;
    message += `│ 📅 Date: ${formData.date}\n`;
    if (formData.time) message += `│ ⏰ Heure: ${formData.time}\n`;
    if (formData.flightNumber) message += `│ ✈️ Vol: ${formData.flightNumber}\n`;
    message += `│ 👥 Passagers: ${formData.passengers}\n`;
    message += `│ 🚗 Véhicule: ${formData.vehicleType}\n`;
    if (formData.occasion) message += `│ 🎉 Occasion: ${formData.occasion}\n`;
    message += `└─────────────────────\n`;

    if (formData.message) {
      message += `\n💬 *Note:* _${formData.message}_\n`;
    }

    message += `\n━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `   _Merci de votre confiance_`;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    setStep(1);
    setFormData({
      departure: '', arrival: '', date: '', time: '', flightNumber: '',
      hours: '', passengers: '', vehicleType: '', occasion: '', message: '',
      firstName: '', lastName: '', company: '', phone: '', email: ''
    });
    setIsSubmitting(false);
  };

  const renderStep1 = () => (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
      <div className="md:col-span-1">
        <Input
          placeholder="Adresse de départ"
          value={formData.departure}
          onChange={(e) => handleInputChange('departure', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
        />
      </div>
      {activeTab !== 'hourly' ? (
        <div className="md:col-span-1">
          <Input
            placeholder="Adresse d'arrivée"
            value={formData.arrival}
            onChange={(e) => handleInputChange('arrival', e.target.value)}
            className="h-12 border-gray-200 focus:border-gray-400"
          />
        </div>
      ) : (
        <div className="md:col-span-1">
          <Input
            placeholder="Nombre d'heures"
            type="number"
            value={formData.hours}
            onChange={(e) => handleInputChange('hours', e.target.value)}
            className="h-12 border-gray-200 focus:border-gray-400"
          />
        </div>
      )}
      <div className="md:col-span-1">
        <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5 font-medium">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" /></svg>
          Date
        </label>
        <Input
          type="date"
          value={formData.date}
          onChange={(e) => handleInputChange('date', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
        />
      </div>
      {activeTab === 'airport' ? (
        <div className="md:col-span-1">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5 font-medium">
            <Plane size={14} />
            N° de vol
          </label>
          <Input
            placeholder="Numéro de vol"
            value={formData.flightNumber}
            onChange={(e) => handleInputChange('flightNumber', e.target.value)}
            className="h-12 border-gray-200 focus:border-gray-400"
          />
        </div>
      ) : (
        <div className="md:col-span-1">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5 font-medium">
            <Clock size={14} />
            Heure
          </label>
          <Input
            type="time"
            value={formData.time}
            onChange={(e) => handleInputChange('time', e.target.value)}
            className="h-12 border-gray-200 focus:border-gray-400"
          />
        </div>
      )}
      <div className="md:col-span-1">
        <Button
          onClick={handleNext}
          className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white rounded-md"
        >
          Suivant
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            placeholder="Nombre de passagers"
            type="number"
            value={formData.passengers}
            onChange={(e) => handleInputChange('passengers', e.target.value)}
            className="h-12 border-gray-200 focus:border-gray-400"
          />
        </div>
        <div>
          <Select
            value={formData.vehicleType}
            onValueChange={(value) => handleInputChange('vehicleType', value)}
          >
            <SelectTrigger className="h-12 border-gray-200">
              <SelectValue placeholder="Type de véhicule" />
            </SelectTrigger>
            <SelectContent>
              {vehicleTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {activeTab === 'hourly' && (
          <div>
            <Select
              value={formData.occasion}
              onValueChange={(value) => handleInputChange('occasion', value)}
            >
              <SelectTrigger className="h-12 border-gray-200">
                <SelectValue placeholder="Occasion" />
              </SelectTrigger>
              <SelectContent>
                {occasions.map((occ) => (
                  <SelectItem key={occ.value} value={occ.value}>
                    {occ.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div>
        <textarea
          placeholder="Message (optionnel)"
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          className="w-full h-24 p-3 border border-gray-200 rounded-md focus:outline-none focus:border-gray-400 resize-none"
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
        <Button
          onClick={handlePrev}
          variant="outline"
          className="h-12 px-6 border-gray-300 order-2 sm:order-1"
        >
          <ChevronLeft size={18} className="mr-1" /> Précédent
        </Button>
        <Button
          onClick={handleNext}
          className="h-12 px-6 bg-gray-900 hover:bg-gray-800 text-white flex-1 order-1 sm:order-2 transition-transform duration-200 hover:scale-[1.02]"
        >
          Suivant <ChevronRight size={18} className="ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          placeholder="Prénom Nom"
          value={formData.firstName}
          onChange={(e) => handleInputChange('firstName', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
          required
        />
        <Input
          placeholder="Entreprise (optionnel)"
          value={formData.company}
          onChange={(e) => handleInputChange('company', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
        />
        <Input
          placeholder="Téléphone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
          required
        />
        <Input
          placeholder="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className="h-12 border-gray-200 focus:border-gray-400"
          required
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4">
        <Button
          type="button"
          onClick={handlePrev}
          variant="outline"
          className="h-12 px-6 border-gray-300 order-2 sm:order-1"
        >
          <ChevronLeft size={18} className="mr-1" /> Précédent
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="h-12 px-8 bg-gray-900 hover:bg-gray-800 text-white flex-1 order-1 sm:order-2 transition-all duration-200 hover:scale-[1.02] disabled:opacity-70"
        >
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
        </Button>
      </div>
    </form>
  );

  return (
    <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 transition-all duration-300">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setStep(1); }}
            className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 ${activeTab === tab.id
                ? 'bg-gray-900 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            <tab.icon size={14} className="sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* Progress indicator */}
      <div className="flex gap-2 mb-4 sm:mb-6">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`h-1 flex-1 rounded-full transition-all duration-500 ${s <= step ? 'bg-gray-900' : 'bg-gray-200'
              }`}
          />
        ))}
      </div>

      {/* Step labels for mobile */}
      <div className="sm:hidden text-center mb-4">
        <span className="text-xs text-gray-500">
          Étape {step}/3 : {step === 1 ? 'Trajet' : step === 2 ? 'Détails' : 'Contact'}
        </span>
      </div>

      {/* Form steps */}
      <div className="transition-all duration-300">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default BookingForm;
